import { useAppConfig } from "@core/composables/data/useAppConfig";
import { ensureTranslationNamespaces } from "@core/composables/state/useLanguage";
import { canAccessRestrictedView } from "@core/utils/accessPolicy";
import { clearCardFlowContext, registerCardFlowAction } from "@core/utils/cardFlow";
import type { NavigationGuardWithThis } from "vue-router";

import { translateLegacyHash } from "../legacy";
import { buildAuthRedirectUrl, extractAccountNsAction } from "../utils";

import { resolveNamespacesForRoute } from "@/translations";
import { setPendingSubscriptionPurchaseAction } from "@/utils/subscriptionPurchase";

const routeRequiredParams: Record<string, string[]> = {
  "map-area": ["areaId"],
  "map-area-details": ["areaId"],
  "map-lot-details": ["lotId"],
  "subscription-details": ["id"],
  "subscription-purchase": ["templateId"],
  "subscription-renew": ["id"],
  "edit-vehicle": ["id"],
  "edit-payment-source": ["id"]
};

function hasRequiredParams(routeName: string, params: Record<string, string> | undefined): boolean {
  const requiredParams = routeRequiredParams[routeName];
  if (requiredParams === undefined || requiredParams.length === 0) {
    return true;
  }
  if (params === undefined) {
    return false;
  }
  return requiredParams.every(param => {
    const value = params[param];
    return typeof value === "string" && value.trim().length > 0;
  });
}

export function createTranslationGuard(): NavigationGuardWithThis<undefined> {
  return async to => {
    const namespaces = resolveNamespacesForRoute(typeof to.name === "string" ? to.name : null);
    if (namespaces.length === 0) {
      return true;
    }
    try {
      await ensureTranslationNamespaces(namespaces);
    } catch (error) {
      console.error("[App] Failed to load route translations", error);
    }
    return true;
  };
}

export function createAuthGuard(base: string): NavigationGuardWithThis<undefined> {
  const { config: appConfig } = useAppConfig();
  const anonymousRouteNames = new Set<string>(["sign-in", ...appConfig.publicViews]);

  return to => {
    if (to.matched.length === 0) {
      return { name: "error-page-not-found" };
    }

    if (to.name === "sign-in") {
      return !appConfig.isAuthenticated ? true : { name: "payment", replace: true };
    }

    const subscriptionIdQuery = Array.isArray(to.query?.subscriptionId)
      ? to.query?.subscriptionId[0]
      : to.query?.subscriptionId;
    if (typeof subscriptionIdQuery === "string" && subscriptionIdQuery.trim().length > 0 && to.name !== "bookings") {
      return {
        name: "subscription-details",
        params: { id: subscriptionIdQuery.trim() },
        replace: true
      };
    }

    const purchaseTemplateQuery = Array.isArray(to.query?.accountRenderBuySubscriptionTemplateId)
      ? to.query?.accountRenderBuySubscriptionTemplateId[0]
      : to.query?.accountRenderBuySubscriptionTemplateId;
    if (typeof purchaseTemplateQuery === "string" && purchaseTemplateQuery.trim().length > 0) {
      setPendingSubscriptionPurchaseAction(extractAccountNsAction(to.query?.accountNsAction));
      return {
        name: "subscription-purchase",
        params: { templateId: purchaseTemplateQuery.trim() },
        replace: true
      };
    }

    const queryActionRaw = extractAccountNsAction(to.query?.accountNsAction);
    if (typeof queryActionRaw === "string" && queryActionRaw.trim().length > 0) {
      const context = registerCardFlowAction(queryActionRaw);
      if (context?.originRoute && hasRequiredParams(context.originRoute, context.routeParams)) {
        const toParams = to.params as Record<string, string> | undefined;
        if (to.name === context.originRoute && hasRequiredParams(context.originRoute, toParams)) {
          return true;
        }
        return {
          name: context.originRoute,
          params: context.routeParams ?? {},
          query: context.routeQuery ?? {}
        };
      }
      if (context) {
        clearCardFlowContext();
      }
      if (to.name === "payment-sources") {
        return true;
      }
      return { name: "payment-sources" };
    }

    const translatedRoute = translateLegacyHash(to.hash);
    if (translatedRoute !== undefined) {
      window.history.replaceState(null, "", window.location.pathname);
      return translatedRoute;
    }

    const routeName = typeof to.name === "string" ? to.name : undefined;
    if (!appConfig.isAuthenticated && routeName !== undefined && !anonymousRouteNames.has(routeName)) {
      return {
        name: "sign-in",
        query: { RedirectUrl: buildAuthRedirectUrl(base, to.fullPath) }
      };
    }

    const organizationId = typeof to.params.organizationId === "string" ? to.params.organizationId : undefined;
    if (
      appConfig.isAuthenticated &&
      routeName !== undefined &&
      !canAccessRestrictedView(appConfig, routeName, organizationId)
    ) {
      return { name: "error-forbidden" };
    }

    return true;
  };
}
